import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const PAGE_SIZE = 25;

/**
 * Owns all admin data state: stats, paginated phrases, LLM catalog, settings.
 * Exposes fetchers and mutations. Consumed once in Admin.jsx and shared to panels.
 */
export default function useAdminData() {
  const [stats, setStats] = useState(null);
  const [phrases, setPhrases] = useState([]);
  const [filterDiff, setFilterDiff] = useState("easy");
  const [filterSource, setFilterSource] = useState("");
  const [loadingList, setLoadingList] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [modelsCatalog, setModelsCatalog] = useState(null);
  const [settings, setSettings] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch {
      toast.error("Failed to load stats");
    }
  }, []);

  const fetchPhrases = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = { difficulty: filterDiff, page, page_size: PAGE_SIZE };
      if (filterSource) params.source = filterSource;
      const { data } = await axios.get(`${API}/admin/phrases`, { params });
      setPhrases(data.items);
      setTotalPages(data.total_pages);
      setTotalCount(data.total);
    } catch {
      toast.error("Failed to load phrases");
    } finally {
      setLoadingList(false);
    }
  }, [filterDiff, filterSource, page]);

  const fetchModelsAndSettings = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/models`),
        axios.get(`${API}/admin/settings`),
      ]);
      setModelsCatalog(mRes.data);
      setSettings(sRes.data);
    } catch {
      toast.error("Failed to load LLM settings");
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchModelsAndSettings();
  }, [fetchStats, fetchModelsAndSettings]);

  // Reload phrases on filter / page change
  useEffect(() => {
    fetchPhrases();
  }, [fetchPhrases]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filterDiff, filterSource]);

  // ---------- Mutations ----------
  const addPhrase = useCallback(async (phrase, difficulty) => {
    await axios.post(`${API}/admin/phrases`, {
      phrase,
      difficulty: difficulty || undefined,
    });
    await Promise.all([fetchStats(), fetchPhrases()]);
  }, [fetchStats, fetchPhrases]);

  const deletePhrase = useCallback(async (id) => {
    await axios.delete(`${API}/admin/phrases/${id}`);
    fetchStats();
    fetchPhrases();
  }, [fetchStats, fetchPhrases]);

  const saveKeys = useCallback(async (payload) => {
    const { data } = await axios.put(`${API}/admin/settings`, payload);
    setSettings(data);
    return data;
  }, []);

  const saveDefaults = useCallback(async (provider, model) => {
    const { data } = await axios.put(`${API}/admin/settings`, {
      default_provider: provider,
      default_model: model,
    });
    setSettings(data);
    return data;
  }, []);

  const importPhrases = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/admin/import`, payload);
    await Promise.all([fetchStats(), fetchPhrases()]);
    return data;
  }, [fetchStats, fetchPhrases]);

  const exportCsv = useCallback(async () => {
    const res = await axios.get(`${API}/admin/phrases/export`, {
      responseType: "blob",
    });
    return new Blob([res.data], { type: "text/csv" });
  }, []);

  const importCsv = useCallback(async (file, mode) => {
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    const { data } = await axios.post(
      `${API}/admin/phrases/import-csv`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    await Promise.all([fetchStats(), fetchPhrases()]);
    return data;
  }, [fetchStats, fetchPhrases]);

  return {
    // state
    stats, phrases, loadingList,
    filterDiff, filterSource, page, totalPages, totalCount,
    modelsCatalog, settings,
    // setters
    setFilterDiff, setFilterSource, setPage, setSettings,
    // actions
    fetchStats, fetchPhrases,
    addPhrase, deletePhrase,
    saveKeys, saveDefaults,
    importPhrases, exportCsv, importCsv,
  };
}
